// ── Design tokens, role definitions, status colors, alert styles ──
// Pure data/helpers — no React, no app state.

// Brand palette. Each value reads a CSS variable (defined in index.css :root) with the
// original hex as a fallback, so a tenant can override the whole look at runtime via
// applyTenantTheme() — see shared/tenantTheme.js. With no override, these render exactly
// as before. Keys are unchanged, so every existing `B.token` usage keeps working.
export const B = {
  cyan:        "var(--brand-cyan, #12b5db)",
  cyanDark:    "var(--brand-cyan-dark, #116588)",
  cyanLight:   "var(--brand-cyan-light, #d4f1f9)",
  cyanMid:     "var(--brand-cyan-mid, #7cd4eb)",
  amber:       "var(--brand-amber, #ffb20d)",
  amberDark:   "var(--brand-amber-dark, #603700)",
  amberLight:  "var(--brand-amber-light, #ffedb8)",
  amberMid:    "var(--brand-amber-mid, #ffd06f)",
  black40:     "var(--brand-black40, #999999)",
  sidebar:     "var(--brand-sidebar, #0d3d52)",
  sidebarHov:  "var(--brand-sidebar-hov, rgba(18,181,219,0.15))",
  sidebarAct:  "var(--brand-sidebar-act, rgba(18,181,219,0.22))",
};

// Three access tiers. Functional access (funding, underwriting, bank creds, etc.) lives in the
// per-user is* flags — Role only governs breadth: edit / delete / audit / manage-users / admin.
export const ROLES = {
  Admin:           { label:"Admin",         color:"#0d3d52",  bg:B.cyanLight,  canEdit:true,  canDelete:true,  canViewAudit:true,  canManageUsers:true,  isAdmin:true  },
  "Standard User": { label:"Standard User", color:B.cyanDark, bg:B.cyanLight,  canEdit:true,  canDelete:false, canViewAudit:true,  canManageUsers:false, isAdmin:false },
  Viewer:          { label:"Viewer",        color:"#555",     bg:"#f0f0f0",    canEdit:false, canDelete:false, canViewAudit:false, canManageUsers:false, isAdmin:false },
};

export const SC = { Active:"#0F6E56",Current:"#0F6E56",Paid:"#0F6E56",Funded:"#0F6E56",Inactive:"#888780",Upcoming:"#185FA5",Pending:"#BA7517",Delinquent:"#993C1D",Overdue:"#993C1D",Denied:"#A32D2D", Forecast:"#185FA5", Actual:"#993C1D","Funding in Progress":"#185FA5", "Accrual":"#0F6E56", "Non Accrual":"#993C1D",
"Paid Off":"#130411", "Terminated":"#888780", "Scheduled":"#185FA5", "Unscheduled":"#BA7517",  "Due":"#993C1D", "Not Due":"#185FA5", "In Process":"#185FA5", "Scheduled":"#185FA5","Unscheduled":"#BA7517","Not Started":"#888780", "In-Progress":"#185FA5", "Completed":"#0F6E56",
"Canceled":"#993C1D", "On Hold":"#BA7517", "Waived":"#3C3489"};

export const SB = { Active:"#E1F5EE",Current:"#E1F5EE",Paid:"#E1F5EE",Funded:"#E1F5EE",Inactive:"#F1EFE8",Upcoming:"#E6F1FB",Pending:"#FAEEDA",Delinquent:"#FAECE7",Overdue:"#FAECE7",Denied:"#FCEBEB",Forecast:"#E6F1FB", Actual:"#FAECE7","Funding in Progress":"#E6F1FB", "Accrual":"#E1F5EE", "Non Accrual":"#FAECE7",
"Paid Off":"#F0F0F0", "Terminated":"#F1EFE8", "Scheduled":"#E6F1FB", "Unscheduled":"#FAEEDA",  "Due":"#FAECE7", "Not Due":"#E6F1FB", "In Process":"#E6F1FB","Scheduled":"#E6F1FB","Unscheduled":"#FAEEDA","Not Started":"#F1EFE8", "In-Progress":"#E6F1FB", "Completed":"#E1F5EE",
"Canceled":"#FAECE7", "On Hold":"#FAEEDA", "Waived":"#EEEDFE"};

export const ALERT_STYLES = {
  "Maturing <90 Days": { bg:"#fdeccd", fg:"#8a5a00" },
  "Past Due":          { bg:"#f7d9cf", fg:"#993C1D" },
  "Covenant Overdue":  { bg:"#ede3f5", fg:"#7B3F8C" },
  "Tax Finding":       { bg:"#F5E1E9", fg:"#8C2F5B" },
  "MCA Activity":      { bg:"#E6F7FC", fg:"#116588" },
  "Bank Activity":     { bg:"#E3EEF9", fg:"#185FA5" },
};

export const alertsFor = a => {
  const out = [];
  if (a.AlertMaturing) out.push("Maturing <90 Days");
  if (a.AlertPastDue)  out.push("Past Due");
  if (a.AlertCovenant) out.push("Covenant Overdue");
  if (a.AlertTax)      out.push("Tax Finding");     // open item on Tax Monitoring's worklist
  if (a.AlertMCA)      out.push("MCA Activity");    // open finding on MCA Monitor
  if (a.AlertBank)     out.push("Bank Activity");   // open alert on Bank Activity
  return out;
};

export const alertChip = key => ({ display:"inline-block", padding:"1px 6px", borderRadius:4, fontSize:10, fontWeight:600, whiteSpace:"nowrap", background:ALERT_STYLES[key].bg, color:ALERT_STYLES[key].fg });

// ── Brand status colors for status bars/charts ──
//   Blue palette → Accrual, Paid Off, Funding in Progress
//   Gold palette → Non Accrual, Charged Off
export const STATUS_COLORS = {
  "Accrual":             "var(--brand-cyan, #12b5db)",       // brand cyan (primary)
  "Paid Off":            "var(--brand-cyan-dark, #116588)",  // brand dark blue
  "Funding in Progress": "#A5E3F1",                          // brand light blue (38% of primary)
  "Non Accrual":         "var(--brand-amber, #ffb20d)",      // brand gold
  "Charged Off":         "var(--brand-amber-dark, #603700)", // brand dark gold/brown
  "Charge Off":          "var(--brand-amber-dark, #603700)", // loans use this label
};
export const statusColor = (s) => STATUS_COLORS[s] || "#9aa0a6";

// ── Chart palettes ────────────────────────────────────────────────────────
//
// Built from the B tokens above, so a tenant override reaches the charts too.
// Every fill here is a CSS variable; nothing downstream should hardcode a hex.
//
// Each entry carries its own INK because a chart draws labels ON these fills,
// and the right text color depends on the fill. Contrast cannot be computed at
// runtime — `B.cyan` is the string "var(--brand-cyan, #12b5db)", not a color —
// so the pairing is decided here, once, where the palette is defined. Each ink
// below clears 4.5:1 against its fill at the default palette. A tenant that
// overrides a brand color to a very different lightness should flip the
// matching ink with it; that is why they sit on the same line.

export const INK_ON_LIGHT = "var(--brand-ink-on-light, #1a1a1a)";
export const INK_ON_DARK  = "var(--brand-ink-on-dark, #ffffff)";

// ── surfaces, WITH LITERAL FALLBACKS ──────────────────────────────────────
//
// The --color-* variables ARE now defined, once, in index.css :root. They were
// not for a long time, and in HTML that was invisible: an unresolvable var()
// makes the declaration invalid at computed-value time, and the initial value
// of `background-color` is transparent and of `color` is inherited, so
// everything still looked roughly right.
//
// IN SVG IT IS NOT INVISIBLE. The initial value of `fill` is BLACK and of
// `stroke` is NONE. A var() with no fallback in a chart paints black shapes
// and silently drops outlines. That is what turned states with no loans black
// on the portfolio map, and what made a text halo do nothing at all.
//
// The fallbacks below therefore stay, even though the variables now resolve.
// They are the belt to index.css's braces: same variable, same tenant
// override, but a real colour if a stylesheet is ever missing, reordered, or
// the component is rendered outside the app root. Anything drawn inside an
// <svg> should reference one of these, never a bare var().
//
// THE LITERALS HERE AND THE VALUES IN index.css MUST STAY IN STEP. When the
// two disagree, nothing breaks loudly — the app renders one colour and any
// stranded corner renders the other, which is exactly the class of bug that
// left one filter row slate and every other one black. Change both or neither.
export const SURFACE        = "var(--color-background-primary, #ffffff)";
export const SURFACE_MUTED  = "var(--color-background-secondary, #f4f6f7)";
export const SURFACE_SUNKEN = "var(--color-background-tertiary, #ecf0f2)";
export const INK_PRIMARY    = "var(--color-text-primary, #1a1a1a)";
export const INK_MUTED      = "var(--color-text-secondary, #646d78)";
export const INK_FAINT      = "var(--color-text-tertiary, #7a838d)";
export const HAIRLINE       = "var(--color-border-tertiary, #e6e9eb)";
export const HAIRLINE_FIRM  = "var(--color-border-secondary, #ccd3d8)";

// SEQUENTIAL — "more is darker". Two of them, so a page can show two different
// measures without the reader assuming they share a scale.
//   Gold  → collateral and aging (the collateral analytics charts)
//   Blue  → money in the book (the dashboard state heat map)
export const RAMP_GOLD = [
  { fill: B.amberLight, ink: INK_ON_LIGHT },
  { fill: B.amberMid,   ink: INK_ON_LIGHT },
  { fill: B.amber,      ink: INK_ON_LIGHT },
  { fill: B.amberDark,  ink: INK_ON_DARK  },
];
// Four steps, stopping at cyanDark. B.sidebar would make a natural fifth, but
// a navy that deep reads as black when it fills a large shape — a state on a
// map shaded #0d3d52 looks like a hole in the map rather than the heaviest
// concentration in the book. It stays available for categorical use, where it
// is one hue among several and carries a legend entry to explain itself.
export const RAMP_BLUE = [
  { fill: B.cyanLight,  ink: INK_ON_LIGHT },
  { fill: B.cyanMid,    ink: INK_ON_LIGHT },
  { fill: B.cyan,       ink: INK_ON_LIGHT },
  { fill: B.cyanDark,   ink: INK_ON_DARK  },
];

// CATEGORICAL — hue says "which", not "how much".
//
// Two rules hold this order together, and both matter:
//
//   1. NO TWO NEIGHBOURS FROM THE SAME FAMILY. With a two-hue brand palette,
//      family is what separates consecutive series when lightness alone would
//      not, and it keeps the two pale tints off each other.
//   2. STRONGEST COLOURS FIRST. Most charts here draw three to five series, so
//      the first five entries are the ones that decide whether a chart reads.
//      The palest tints sit at the end, where a chart that reaches them has
//      bigger problems than colour. Putting cyanLight third — which an
//      alternating-only order does — makes the third series of every
//      three-series chart a near-white band on a white card.
//
// Charts index this modulo its length. Past ten series colour alone stops
// carrying the distinction, and the answer is a different cut of the data — a
// filter, or splitting the chart — not an eleventh colour.
export const RAMP_CATEGORICAL = [
  { fill: B.cyan,       ink: INK_ON_LIGHT },   // blue,    mid
  { fill: B.amberDark,  ink: INK_ON_DARK  },   // gold,    dark
  { fill: B.cyanMid,    ink: INK_ON_LIGHT },   // blue,    light
  { fill: B.amber,      ink: INK_ON_LIGHT },   // gold,    mid
  { fill: B.cyanDark,   ink: INK_ON_DARK  },   // blue,    dark
  { fill: B.amberMid,   ink: INK_ON_LIGHT },   // gold,    light
  { fill: B.sidebar,    ink: INK_ON_DARK  },   // blue,    darkest
  { fill: B.amberLight, ink: INK_ON_LIGHT },   // gold,    palest
  { fill: B.black40,    ink: INK_ON_LIGHT },   // neutral, mid
  { fill: B.cyanLight,  ink: INK_ON_LIGHT },   // blue,    palest
];

// Pick from a ramp with wraparound. Returns { fill, ink } — always use both.
export const rampAt = (ramp, i) => ramp[((i % ramp.length) + ramp.length) % ramp.length];
